import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Save, X, IndianRupee, Users } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 text-right';
const label = 'block text-xs font-medium text-gray-500 mb-1';

const empty = { basic:0, hra:0, da:0, transportAllowance:0, otherAllowance:0, pfEmployee:0, esiEmployee:0, professionalTax:0 };

export default function SalaryStructures() {
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const myStaffId = currentUser?.profileId || '';

  const [staff,       setStaff]       = useState([]);
  const [structures,  setStructures]  = useState([]);
  const [editing,     setEditing]     = useState(null); // staffId being edited
  const [form,        setForm]        = useState(empty);
  const [saving,      setSaving]      = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, ss] = await Promise.all([api.list('staff'), api.getSalaryStructures()]);
      setStaff(st.filter(s => s.status === 'active'));
      setStructures(ss);
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getStructure = (staffId) => structures.find(s => s.staffId === staffId);

  const openEdit = (s) => {
    const ss = getStructure(s.id) || {};
    setForm({
      basic:              ss.basic              || 0,
      hra:                ss.hra                || 0,
      da:                 ss.da                 || 0,
      transportAllowance: ss.transportAllowance || 0,
      otherAllowance:     ss.otherAllowance     || 0,
      pfEmployee:         ss.pfEmployee         || 0,
      esiEmployee:        ss.esiEmployee        || 0,
      professionalTax:    ss.professionalTax    || 0,
    });
    setEditing(s.id);
  };

  const save = async (staffId) => {
    if (!form.basic || Number(form.basic) <= 0) { alert('Basic salary is required'); return; }
    setSaving(true);
    try {
      await api.saveSalaryStructure({ staffId, ...form });
      await load();
      setEditing(null);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const gross = Number(form.basic||0)+Number(form.hra||0)+Number(form.da||0)+Number(form.transportAllowance||0)+Number(form.otherAllowance||0);
  const deductions = Number(form.pfEmployee||0)+Number(form.esiEmployee||0)+Number(form.professionalTax||0);
  const net = gross - deductions;

  const filtered = staff.filter(s => {
    if (isTeacher) return s.id === myStaffId;
    return `${s.firstName} ${s.lastName} ${s.designation}`.toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <div className="text-center py-16 text-gray-400">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{isTeacher ? 'My Salary' : 'Salary Structures'}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{isTeacher ? 'Your salary components and breakdown' : 'Configure salary components for each staff member'}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
          <Users size={14} /> {staff.length} active staff
        </div>
      </div>

      {!isTeacher && (
        <input
          type="text" placeholder="Search staff…"
          value={search} onChange={e => setSearch(e.target.value)}
          className="mb-4 px-4 py-2 border border-gray-300 rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
      )}

      <div className="space-y-3">
        {filtered.map(s => {
          const ss = getStructure(s.id);
          const isEditing = editing === s.id;
          const gross_ss = ss ? (Number(ss.basic)||0)+(Number(ss.hra)||0)+(Number(ss.da)||0)+(Number(ss.transportAllowance)||0)+(Number(ss.otherAllowance)||0) : 0;

          return (
            <div key={s.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Header row */}
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold">
                    {s.firstName?.[0]}{s.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{s.firstName} {s.lastName}</p>
                    <p className="text-xs text-gray-400">{s.designation} · ID: {s.employeeId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {ss ? (
                    <>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Gross</p>
                        <p className="font-semibold text-gray-700">₹{Math.round(gross_ss).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Net (est.)</p>
                        <p className="font-bold text-green-600">₹{Math.round(gross_ss - (Number(ss.pfEmployee)||0) - (Number(ss.esiEmployee)||0) - (Number(ss.professionalTax)||0)).toLocaleString('en-IN')}</p>
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-orange-500 font-medium">Not configured</span>
                  )}
                  {!isTeacher && (
                    <button
                      onClick={() => isEditing ? setEditing(null) : openEdit(s)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${isEditing ? 'bg-gray-100 text-gray-600' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>
                      {isEditing ? <><X size={13} /> Cancel</> : <><Edit2 size={13} /> {ss ? 'Edit' : 'Set'} Salary</>}
                    </button>
                  )}
                </div>
              </div>

              {/* Edit form */}
              {isEditing && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Earnings */}
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Earnings</p>
                      <div className="space-y-2">
                        {[['basic','Basic Salary *'],['hra','HRA'],['da','DA'],['transportAllowance','Transport Allow.'],['otherAllowance','Other Allowance']].map(([k,lbl]) => (
                          <div key={k}>
                            <label className={label}>{lbl}</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                              <input type="number" min="0" value={form[k]}
                                onChange={e => setForm(p => ({...p, [k]: e.target.value}))}
                                className={`${inp} pl-7`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Deductions + summary */}
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Deductions</p>
                      <div className="space-y-2">
                        {[['pfEmployee','PF (Employee)'],['esiEmployee','ESI (Employee)'],['professionalTax','Professional Tax']].map(([k,lbl]) => (
                          <div key={k}>
                            <label className={label}>{lbl}</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                              <input type="number" min="0" value={form[k]}
                                onChange={e => setForm(p => ({...p, [k]: e.target.value}))}
                                className={`${inp} pl-7`} />
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Live summary */}
                      <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                        <Row label="Gross Salary" value={gross} color="text-gray-800" />
                        <Row label="Total Deductions" value={deductions} color="text-red-500" prefix="-" />
                        <div className="border-t border-gray-100 pt-2">
                          <Row label="Net Salary" value={net} color="text-green-600 font-bold text-base" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      disabled={saving}
                      onClick={() => save(s.id)}
                      className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                      <Save size={14} /> {saving ? 'Saving…' : 'Save Salary Structure'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, value, color, prefix='' }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm ${color}`}>{prefix}₹{Math.round(value).toLocaleString('en-IN')}</span>
    </div>
  );
}
